export type PublicSiteId = 'default' | 'security-walk' | 'construction-walk'

/** Primary production domain for the security-side experience (Patrol Walk). */
export const PATROL_WALK_CANONICAL_HOST = 'patrolwalk.com'

/** Primary production domain for the construction-side experience (ConstructionWalk). */
export const CONSTRUCTION_WALK_CANONICAL_HOST = 'cswalk.com'

/** Primary production domain for the umbrella SiteWalk experience. */
export const SITE_WALK_CANONICAL_HOST = 'sitewalker.org'

/** Primary production domain for StorageWalk. */
export const STORAGE_WALK_CANONICAL_HOST = 'storagewalk.com'

/** All hostnames that resolve to the security-side (Patrol Walk) experience. */
const SECURITY_SIDE_HOSTS = new Set<string>([
  PATROL_WALK_CANONICAL_HOST,
  'security-walk.com',
])

/** All hostnames that resolve to the construction-side (ConstructionWalk) experience. */
const CONSTRUCTION_SIDE_HOSTS = new Set<string>([
  CONSTRUCTION_WALK_CANONICAL_HOST,
])

/** Local dev: default to Storage Walk (explicit product selection via query param). */
const LOCAL_DEV_HOSTS = new Set<string>([
  'localhost',
  '127.0.0.1',
  '::1',
])

/** Strip leading `www.` for consistent multi-domain routing (case-insensitive). */
export function normalizeHostname(hostname: string): string {
  const h = hostname.trim().toLowerCase()
  return h.startsWith('www.') ? h.slice(4) : h
}

/**
 * Get product override from URL query param (for localhost dev).
 * Supports: ?product=storage | ?product=patrol | ?product=construction
 */
function getProductOverrideFromQuery(): PublicSiteId | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  const product = params.get('product')?.toLowerCase()
  switch (product) {
    case 'storage':
      return 'default'
    case 'patrol':
      return 'security-walk'
    case 'construction':
      return 'construction-walk'
    default:
      return null
  }
}

export function isConstructionWalk(siteId?: PublicSiteId): boolean {
  return (siteId ?? getPublicSiteId()) === 'construction-walk'
}

export function getPublicSiteId(hostname?: string): PublicSiteId {
  const override = getProductOverrideFromQuery()
  if (override) return override

  const raw =
    hostname ??
    (typeof window !== 'undefined' ? window.location.hostname : '')
  const normalized = normalizeHostname(raw)

  if (SECURITY_SIDE_HOSTS.has(normalized)) return 'security-walk'
  if (CONSTRUCTION_SIDE_HOSTS.has(normalized)) return 'construction-walk'
  return 'default'
}

/**
 * Browser tab title for the current (or given) host.
 * Static HTML already uses these names; JS must not overwrite them with
 * default branding (`Storage Walk`) on sitewalker.org / storagewalk.com.
 */
export function getDocumentTitle(hostname?: string): string {
  const raw =
    hostname ??
    (typeof window !== 'undefined' ? window.location.hostname : '')
  const normalized = normalizeHostname(raw)

  if (normalized === SITE_WALK_CANONICAL_HOST) return 'SiteWalk'
  if (normalized === STORAGE_WALK_CANONICAL_HOST) return 'StorageWalk'
  if (SECURITY_SIDE_HOSTS.has(normalized)) return 'PatrolWalk'
  if (CONSTRUCTION_SIDE_HOSTS.has(normalized)) return 'ConstructionWalk'

  const override = getProductOverrideFromQuery()
  if (override === 'security-walk') return 'PatrolWalk'
  if (override === 'construction-walk') return 'ConstructionWalk'
  return 'StorageWalk'
}

/**
 * Non-canonical hosts redirect to canonical domain.
 * - security-walk.com → patrolwalk.com
 * Skips localhost / loopback so local dev keeps working.
 * Call once before React mounts to avoid a visible flash.
 */
export function redirectToCanonicalHostIfNeeded(): void {
  if (typeof window === 'undefined') return
  const normalized = normalizeHostname(window.location.hostname)

  if (LOCAL_DEV_HOSTS.has(normalized)) return

  let canonicalHost: string | null = null

  if (SECURITY_SIDE_HOSTS.has(normalized) && normalized !== PATROL_WALK_CANONICAL_HOST) {
    canonicalHost = PATROL_WALK_CANONICAL_HOST
  } else if (CONSTRUCTION_SIDE_HOSTS.has(normalized) && normalized !== CONSTRUCTION_WALK_CANONICAL_HOST) {
    canonicalHost = CONSTRUCTION_WALK_CANONICAL_HOST
  }

  if (canonicalHost) {
    const { pathname, search, hash } = window.location
    window.location.replace(
      `https://${canonicalHost}${pathname}${search}${hash}`,
    )
  }
}

/** @deprecated Use redirectToCanonicalHostIfNeeded() instead */
export const redirectSecuritySideHostToPatrolWalkIfNeeded = redirectToCanonicalHostIfNeeded
